import { AutocompleteCarrierInput, FreeFormCarrierInput } from "./CarrierInput";
import { Error as ErrorIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import * as numbers from "common/numbers";
import ConditionalTooltip from "components/common/ConditionalTooltip";
import FeatureFlagged from "components/common/FeatureFlagged";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import { DYNAMIC_AUTO_COMPLETE } from "flags";
import * as _ from "lodash";
import { memo } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const ShareTableRow = ({
  share,
  layerIndex,
  shareIndex,
  errors,
  updateShare,
  deleteShare,
  autoFocus,
}) => {
  const carrierChanged = (value) =>
    updateShare({
      layerIndex,
      shareIndex,
      values: {
        carrier: value,
      },
    });
  return (
    <TableRow>
      <TableCell>
        {!!deleteShare && (
          <IconButton
            icon={DeleteIcon}
            onClick={() => deleteShare({ layerIndex, shareIndex })}
          />
        )}
      </TableCell>
      <TableCell>
        {" "}
        <FeatureFlagged
          on={AutocompleteCarrierInput}
          off={FreeFormCarrierInput}
          flag={DYNAMIC_AUTO_COMPLETE}
          value={share.carrier ?? ""}
          onChangeValue={carrierChanged}
          autoFocus={autoFocus}
        />
      </TableCell>
      <Visible byTag={"<BrokerTower>.table.share.coverage"} defaultVisible>
        <TableCell>
          <TextField
            value={share.coverage ?? ""}
            onChange={(e) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  coverage: e.target.value,
                },
              })
            }
            variant={"filled"}
            autoComplete={"off"}
            size={"small"}
            fullWidth
            hiddenLabel
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.shareOfLimit"} defaultVisible>
        <TableCell>
          <PrettyNumberTextField
            value={share.shareOfLimit ?? ""}
            stringValue={String(share.shareOfLimit ?? "")}
            onChangeNumber={(value) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: { shareOfLimit: value },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.share"} defaultVisible>
        <TableCell>
          <ConditionalTooltip conditional={errors?.share} title={errors?.share}>
            <PrettyNumberTextField
              value={share.share ?? ""}
              stringValue={String(share.share ?? "")}
              onChangeNumber={(value) =>
                updateShare({
                  layerIndex,
                  shareIndex,
                  values: { share: value },
                })
              }
              inputProps={{ style: { textAlign: "right" } }}
              variant={"filled"}
              size={"small"}
              error={errors?.share}
              hiddenLabel
              fullWidth
            />
          </ConditionalTooltip>
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.shareOfGwp"} defaultVisible>
        <TableCell>
          <PrettyNumberTextField
            value={share.shareOfGrossPremium}
            stringValue={String(share.shareOfGrossPremium ?? "")}
            onChangeNumber={(value) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  shareOfGrossPremium: value,
                  grossRPM: null,
                },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.grossRPM"} defaultVisible>
        <TableCell>
          <PrettyNumberTextField
            value={share.grossRPM}
            stringValue={String(share.grossRPM ?? "")}
            onChangeNumber={(value) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  shareOfGrossPremium: null,
                  grossRPM: value,
                },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.brokerage"} defaultVisible>
        <TableCell>
          <PrettyNumberTextField
            value={share.brokerage}
            stringValue={String(share.brokerage ?? "")}
            onChangeNumber={(value) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: { brokerage: value },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.shareOfNwp"} defaultVisible>
        <TableCell>
          <PrettyNumberTextField
            value={share.shareOfNetPremium}
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            disabled
          />
        </TableCell>
      </Visible>
      <Visible
        byTag={"<BrokerTower>.table.share.retroactiveDate"}
        defaultVisible={false}
      >
        <TableCell>
          <FormattedDatePicker
            compact
            clearable={false}
            value={share.retroactiveDate ?? ""}
            onChangeDate={(date) => {
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  retroactiveDate: date,
                },
              });
            }}
          />
        </TableCell>
      </Visible>
      <Visible
        byTag={"<BrokerTower>.table.share.policyForm"}
        defaultVisible={false}
      >
        <TableCell>
          <TextField
            value={share.policyForm ?? ""}
            onChange={(e) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: { policyForm: e.target.value },
              })
            }
            variant={"filled"}
            autoComplete={"off"}
            size={"small"}
            fullWidth
            hiddenLabel
          />
        </TableCell>
      </Visible>
      <Visible
        byTag={"<BrokerTower>.table.share.aggregateLimit"}
        defaultVisible={false}
      >
        <TableCell>
          <PrettyNumberTextField
            value={share.shareOfAggregateLimit}
            onChangeNumber={(shareOfAggregateLimit) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: { shareOfAggregateLimit },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible
        byTag={"<BrokerTower>.table.share.plAggregateLimit"}
        defaultVisible={false}
      >
        <TableCell>
          <PrettyNumberTextField
            value={share.shareOfPLAggregateLimit}
            onChangeNumber={(shareOfPLAggregateLimit) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: { shareOfPLAggregateLimit },
              })
            }
            inputProps={{ style: { textAlign: "right" } }}
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.comments"} defaultVisible>
        <TableCell>
          <TextField
            value={share.comments ?? ""}
            onChange={(e) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  comments: e.target.value,
                },
              })
            }
            variant={"filled"}
            autoComplete={"off"}
            size={"small"}
            fullWidth
            hiddenLabel
          />
        </TableCell>
      </Visible>
      <Visible byTag={"<BrokerTower>.table.share.reference"} defaultVisible>
        <TableCell>
          <TextField
            value={share.reference ?? ""}
            onChange={(e) =>
              updateShare({
                layerIndex,
                shareIndex,
                values: {
                  reference: e.target.value,
                },
              })
            }
            variant={"filled"}
            autoComplete={"off"}
            size={"small"}
            fullWidth
            hiddenLabel
          />
        </TableCell>
      </Visible>
    </TableRow>
  );
};

const MemoizedShareTableRow = memo(ShareTableRow, _.isEqual);

const ShareTable = ({ layer, layerIndex, updateShare, deleteShare }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const shares = layer?.shares ?? [];
  const errors = {
    shareOfLimit:
      !layer?.limit ||
      numbers.approxEqual(
        (shares ?? [])
          .map((s) => s.shareOfLimit || 0)
          .reduce((a, b) => a + b, 0),
        layer?.limit,
        { epsilon: 5_000 }
      )
        ? null
        : "Shares of limit do not sum to the total",
    shareOfGrossPremium:
      !layer?.grossPremium ||
      numbers.approxEqual(
        (shares ?? [])
          .map((s) => s.shareOfGrossPremium || 0)
          .reduce((a, b) => a + b, 0),
        layer?.grossPremium,
        { epsilon: 5_000 }
      )
        ? null
        : "Shares of gross premium do not sum to the total",
  };
  return (
    <Table size={"small"}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 60 }}></TableCell>
          <TableCell sx={{ width: 350 }} align={"left"}>
            {translateLabel(labels.TOWER_SHARE_TABLE_CARRIER, {
              default: "Carrier",
            })}
          </TableCell>
          <Visible byTag={"<BrokerTower>.table.share.coverage"} defaultVisible>
            <TableCell sx={{ width: 200 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_COVERAGE, {
                default: "Coverage",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.shareOfLimit"}
            defaultVisible
          >
            <TableCell sx={{ width: 300 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_SHARE_OF_LIMIT, {
                default: "Share of Limit",
              })}
              {errors.shareOfLimit && (
                <Visible
                  byTag={"<BrokerTower>.table.share.shareOfLimit.showErrors"}
                  defaultVisible={false}
                >
                  <Tooltip title={errors.shareOfLimit}>
                    <ErrorIcon
                      color={"error"}
                      sx={{ fontSize: "1rem", marginLeft: 0.5 }}
                    />
                  </Tooltip>
                </Visible>
              )}
            </TableCell>
          </Visible>
          <Visible byTag={"<BrokerTower>.table.share.share"} defaultVisible>
            <TableCell sx={{ width: 100 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_SHARE, {
                default: "% Share",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.shareOfGwp"}
            defaultVisible
          >
            <TableCell sx={{ width: 300 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_SHARE_OF_GWP, {
                default: "Share of GWP",
              })}
              {errors.shareOfGrossPremium && (
                <Visible
                  byTag={"<BrokerTower>.table.share.shareOfGwp.showErrors"}
                  defaultVisible
                >
                  <Tooltip title={errors.shareOfGrossPremium}>
                    <ErrorIcon
                      color={"error"}
                      sx={{ fontSize: "1rem", marginLeft: 0.5 }}
                    />
                  </Tooltip>
                </Visible>
              )}
            </TableCell>
          </Visible>
          <Visible byTag={"<BrokerTower>.table.share.grossRPM"} defaultVisible>
            <TableCell sx={{ width: 250 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_GROSS_RPM, {
                default: "Gross RPM",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.brokerage"}
            defaultVisible={true}
          >
            <TableCell sx={{ width: 100 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_BROKERAGE, {
                default: "% Brokerage",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.shareOfNwp"}
            defaultVisible={true}
          >
            <TableCell sx={{ width: 300 }} align={"right"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_SHARE_OF_NWP, {
                default: "Share of NWP",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.retroactiveDate"}
            defaultVisible={false}
          >
            <TableCell sx={{ width: 194 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_RETRO_DATE, {
                default: "Retro Date",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.policyForm"}
            defaultVisible={false}
          >
            <TableCell sx={{ width: 200 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_POLICY_FORM, {
                default: "Policy Form",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.aggregateLimit"}
            defaultVisible={false}
          >
            <TableCell sx={{ width: 300 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_AGGREGATE_LIMIT, {
                default: "Agg Limit",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.plAggregateLimit"}
            defaultVisible={false}
          >
            <TableCell sx={{ width: 300 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_PL_AGGREGATE_LIMIT, {
                default: "PL Agg Limit",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.comments"}
            defaultVisible={true}
          >
            <TableCell sx={{ width: 300 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_COMMENTS, {
                default: "Comments",
              })}
            </TableCell>
          </Visible>
          <Visible
            byTag={"<BrokerTower>.table.share.reference"}
            defaultVisible={true}
          >
            <TableCell sx={{ width: 200 }} align={"left"}>
              {translateLabel(labels.TOWER_SHARE_TABLE_REFERENCE, {
                default: "Reference",
              })}
            </TableCell>
          </Visible>
        </TableRow>
      </TableHead>
      <TableBody>
        {(shares ?? []).concat([{}]).map((share, shareIndex) => (
          <MemoizedShareTableRow
            key={shareIndex}
            share={share}
            layerIndex={layerIndex}
            shareIndex={shareIndex}
            errors={errors}
            updateShare={updateShare}
            deleteShare={
              shareIndex === (shares ?? []).length ? null : deleteShare
            }
            autoFocus={shareIndex === (shares ?? []).length - 1}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default memo(ShareTable, _.isEqual);
